import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CheckCircle, Clear, ContentCopy, RemoveCircleOutline, SelectAll } from "@mui/icons-material";
import { formatSingaporeDate } from "../../../../utils/timezone";
import RFQItemRow from "../RFQItemRow";
import type { RFQ } from "../../../../types/rfq";
import type { TeamMember } from "../../../../types";
import type { DuplicateQuote } from "./useDuplicateQuote";
import type { RfqItemsSelection } from "./useRfqItemsSelection";

interface RfqItemsPanelProps {
  rfq: RFQ;
  selectedUser: TeamMember | null;
  allItemsValidated: boolean;
  duplicateQuote: DuplicateQuote;
  items: RfqItemsSelection;
}

/** "Same items as RFQ #n" with the button that copies that quote's prices. */
function DuplicateBanner({ duplicateQuote: { duplicate, apply, applying }, selectedUser }: Pick<RfqItemsPanelProps, "duplicateQuote" | "selectedUser">) {
  if (!duplicate) return null;
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexWrap: { xs: "wrap", sm: "nowrap" },
        alignItems: "center",
        gap: 1.5,
        mb: 2,
        p: 1.5,
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        borderRadius: 1,
      })}
    >
      <ContentCopy color="primary" />
      <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
        <Typography variant="body2" color="primary.dark" fontWeight={600}>
          {duplicate.quantitiesMatch ? "Identical to" : "Same items as"} RFQ #{duplicate.priorRfqId} — quoted to{" "}
          {duplicate.sameClient
            ? "the same company"
            : `a different company (${duplicate.clientName || duplicate.clientEmail || "unknown"})`}{" "}
          on {formatSingaporeDate(duplicate.quoteSentAt)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          All {duplicate.itemCount} item{duplicate.itemCount === 1 ? "" : "s"} match by
          name{duplicate.quantitiesMatch ? " and quantity" : " (quantities differ — unit prices are copied)"}.
          Applying copies that quote's prices and remarks — you can still edit
          everything before sending.
        </Typography>
      </Box>
      <Tooltip title={selectedUser ? "" : "Select a user first"}>
        <span>
          <Button
            variant="contained"
            size="small"
            disabled={!selectedUser || applying}
            onClick={apply}
            startIcon={
              applying ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ContentCopy />
              )
            }
            sx={{ whiteSpace: "nowrap" }}
          >
            Apply previous quote
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}

/** "Items (n)", the selection count, and the bulk buttons. */
function ItemsHeader({ rfq, items }: Pick<RfqItemsPanelProps, "rfq" | "items">) {
  const { selectedItemIds } = items;
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        columnGap: 2,
        rowGap: 1,
        mb: 1.5,
      }}
    >
      <Typography variant="h6" component="div">
        Items ({(rfq.items || []).length})
        {selectedItemIds.length > 0 && (
          <Typography
            component="span"
            variant="body2"
            sx={{ ml: 1.5, fontWeight: 500, color: "primary.main" }}
          >
            ({selectedItemIds.length} selected)
          </Typography>
        )}
      </Typography>

      {/* Bulk action buttons */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <Tooltip title="Select all unvalidated items">
          <Button
            size="small"
            variant="outlined"
            startIcon={<SelectAll />}
            onClick={items.selectAllUnvalidated}
            sx={{ bgcolor: "background.paper" }}
          >
            Select Unvalidated
          </Button>
        </Tooltip>

        {selectedItemIds.length > 0 && (
          <>
            <Tooltip title="Mark selected items as not available (sets price to $0 and validates)">
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<RemoveCircleOutline />}
                onClick={items.markSelectedNotAvailable}
              >
                Mark Not Available
              </Button>
            </Tooltip>

            <Tooltip title="Clear selection">
              <IconButton
                size="small"
                onClick={items.clearSelection}
                aria-label="Clear selection"
              >
                <Clear />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}

/** The expanded RFQ: duplicate and all-validated banners, bulk actions, item table. */
export default function RfqItemsPanel({ rfq, selectedUser, allItemsValidated, duplicateQuote, items }: RfqItemsPanelProps) {
  return (
    <>
      {rfq.status !== "QUOTED" && <DuplicateBanner duplicateQuote={duplicateQuote} selectedUser={selectedUser} />}

      {(rfq.items || []).length > 0 && allItemsValidated && (
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            px: 1.5,
            py: 1,
            bgcolor: alpha(theme.palette.success.main, 0.08),
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            borderRadius: 1,
          })}
        >
          <CheckCircle color="success" fontSize="small" />
          <Typography variant="body2" color="success.dark" fontWeight={600}>
            All items have been validated and are ready for quoting
          </Typography>
        </Box>
      )}

      <ItemsHeader rfq={rfq} items={items} />

      {/* width 0 + min-width 100%: the item table never widens the RFQ
          table around it; on narrow windows it scrolls inside this box. */}
      <TableContainer
        sx={{
          width: 0,
          minWidth: "100%",
          overflowX: "auto",
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: 1240,
            // Last row's line would double up with the container border
            "& tbody tr:last-of-type > td": { borderBottom: 0 },
          }}
        >
          <TableHead>
            <TableRow>
              {rfq.status !== "QUOTED" && <TableCell padding="checkbox" sx={{ width: 48 }}></TableCell>}
              <TableCell sx={{ minWidth: 220 }}>Name</TableCell>
              <TableCell align="right" sx={{ width: 88 }}>Quantity</TableCell>
              <TableCell sx={{ width: 156 }}>Cost Price</TableCell>
              <TableCell sx={{ width: 188 }}>Quoted Price</TableCell>
              <TableCell sx={{ width: 104 }}>Markup %</TableCell>
              <TableCell sx={{ width: 120 }}>Indent Days</TableCell>
              <TableCell sx={{ width: { xs: 304, xl: "24%" } }}>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.orderedUniqueItems.map((item) => (
              <RFQItemRow
                key={item.rfq_item_id}
                item={item}
                rfqId={rfq.rfq_id}
                canEdit={rfq.status !== "QUOTED"}
                selectedUser={selectedUser}
                rfqStatus={rfq.status}
                isSelected={items.selectedItemIds.includes(item.rfq_item_id)}
                onToggleSelect={items.toggleItem}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
